import { useMemo, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import moment from 'moment'
import { useDebounce } from 'use-debounce'

import OPERATION_FIND_RAN_ROOMS from './operations-gql/queries'
import useFilters, { useSort, useCountItems } from './store'

export const PAGE_SIZE = 1000

const adjustDateByDays = (date, daysToAdd) => {
  if (!date) return ''
  return moment(date).add(daysToAdd, 'day').toISOString()
}

const useRans = () => {
  const page = useRef(1)
  const [sort] = useSort()
  const [, setCountItems] = useCountItems()
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true)
  const { siteId } = useParams()
  const [filters] = useFilters()

  const [debouncedVerbatim] = useDebounce(filters.verbatim, 400)

  const queryVariables = useMemo(
    () => ({
      limit: PAGE_SIZE,
      sf_id: siteId,
      verbatim: debouncedVerbatim,
      created_start_at: adjustDateByDays(filters.created_start_at, 1),
      created_end_at: adjustDateByDays(filters.created_end_at, 2),
      sort_by: sort.by,
      sort_direction: sort.direction
    }),
    [siteId, sort, debouncedVerbatim, filters.created_start_at, filters.created_end_at]
  )

  const { error, loading, data, fetchMore, networkStatus } = useQuery(OPERATION_FIND_RAN_ROOMS, {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ rooms }) => {
      setCountItems(rooms.length)
    }
  })

  const rans = useMemo(() => (!error && data ? data.rooms : []), [error, data])

  return {
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: !loading && rans.length >= PAGE_SIZE && shouldFetchNextPage,
    error,
    rans,
    fetchNextPage: async () => {
      if (networkStatus === 3) {
        return
      }
      page.current++
      const fetchMoreResult = await fetchMore({
        variables: {
          offset: rans.length,
          limit: PAGE_SIZE
        }
      })

      if (!fetchMoreResult.data?.rooms?.length || fetchMoreResult.data?.rooms?.length < PAGE_SIZE) {
        setShouldFetchNextPage(false)
      }
    }
  }
}

export default useRans
